// Google Analytics utility module
// Handles GA initialization, tracking, and privacy compliance

import { _settings } from '../AppSettings';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Storage keys for privacy preferences
const ANALYTICS_CONSENT_KEY = 'analytics_consent';
const ANALYTICS_OPT_OUT_KEY = 'analytics_opt_out';

// Analytics consent status
export enum AnalyticsConsent {
  GRANTED = 'granted',
  DENIED = 'denied',
  PENDING = 'pending'
}

// Initialize Google Analytics
export const initializeGA = (): void => {
  if (!_settings.ENABLE_ANALYTICS || !_settings.GOOGLE_ANALYTICS_ID) {
    console.log('Google Analytics: Disabled or no tracking ID provided');
    return;
  }

  // Check if user has opted out
  if (hasOptedOut()) {
    console.log('Google Analytics: User has opted out');
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // Set initial timestamp
  window.gtag('js', new Date());

  // Configure Google Analytics with privacy settings
  window.gtag('config', _settings.GOOGLE_ANALYTICS_ID, {
    anonymize_ip: true, // Anonymize IP addresses for privacy
    allow_ad_personalization_signals: false, // Disable ad personalization
    allow_google_signals: false, // Disable Google Signals for privacy
    cookie_flags: 'max-age=7776000;secure;samesite=lax', // 90 days, secure cookies
  });

  // Load GA script
  loadGAScript();
};

// Load Google Analytics script dynamically
const loadGAScript = (): void => {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${_settings.GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(script);
};

// Track page views
export const trackPageView = (page_title: string, page_location: string): void => {
  if (!isTrackingEnabled()) return;

  window.gtag('config', _settings.GOOGLE_ANALYTICS_ID, {
    page_title,
    page_location,
  });
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  if (!isTrackingEnabled()) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// Common event tracking functions
export const trackButtonClick = (buttonName: string, location: string): void => {
  trackEvent('click', 'button', `${buttonName}_${location}`);
};

export const trackFormSubmission = (formName: string): void => {
  trackEvent('submit', 'form', formName);
};

export const trackServiceInquiry = (serviceType: string): void => {
  trackEvent('inquiry', 'service', serviceType);
};

export const trackUserRegistration = (): void => {
  trackEvent('sign_up', 'user');
};

export const trackUserLogin = (): void => {
  trackEvent('login', 'user');
};

// Privacy and consent management
export const getAnalyticsConsent = (): AnalyticsConsent => {
  const consent = localStorage.getItem(ANALYTICS_CONSENT_KEY);
  if (!consent) return AnalyticsConsent.PENDING;
  return consent as AnalyticsConsent;
};

export const setAnalyticsConsent = (consent: AnalyticsConsent): void => {
  localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
  
  if (consent === AnalyticsConsent.GRANTED) {
    initializeGA();
  }
};

export const hasOptedOut = (): boolean => {
  return localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === 'true';
};

export const optOutOfAnalytics = (): void => {
  localStorage.setItem(ANALYTICS_OPT_OUT_KEY, 'true');
  localStorage.setItem(ANALYTICS_CONSENT_KEY, AnalyticsConsent.DENIED);
  
  // Disable Google Analytics
  if (window.gtag) {
    window[`ga-disable-${_settings.GOOGLE_ANALYTICS_ID}`] = true;
  }
};

export const optInToAnalytics = (): void => {
  localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
  setAnalyticsConsent(AnalyticsConsent.GRANTED);
};

// Check if tracking is enabled and user has consented
const isTrackingEnabled = (): boolean => {
  return (
    _settings.ENABLE_ANALYTICS &&
    !!_settings.GOOGLE_ANALYTICS_ID &&
    !hasOptedOut() &&
    getAnalyticsConsent() === AnalyticsConsent.GRANTED &&
    !!window.gtag
  );
};

// Initialize analytics if consent has already been granted
export const initializeIfConsented = (): void => {
  if (getAnalyticsConsent() === AnalyticsConsent.GRANTED) {
    initializeGA();
  }
};